// Journal posts. Body paragraphs starting with "## " render as subheadings on the article page.
export const journalPosts = [
  {
    slug: 'algorithmic-precision',
    title: 'Algorithmic precision: why AI is our backbone, not our aesthetic',
    category: 'Labs',
    date: 'Oct 2026',
    readTime: '8 min read',
    excerpt:
      'Inside the thinking behind our AI-OS: agents, memory and workflows run quietly in the engine room, while everything clients see remains handcrafted.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/af99e0c4-776b-4dd5-afeb-0a76c47ea61a.jpg',
    href: '/journal/algorithmic-precision',
    body: [
      'There is a version of AI that shows. Generated faces, synthetic gloss, images that look like everything and nothing. We do not publish that version — and we never will.',
      '## The engine room',
      'Our AI-OS lives behind the work. Agents prepare research, protected memory keeps every project decision retrievable, and automated workflows move assets from one stage to the next. It is infrastructure, in the same way electricity is infrastructure for a workshop.',
      'The result is speed without compromise. Production costs fall by up to 90%, timelines shrink to a fraction, and the people at LAEBEL spend their time where it matters: on judgement, taste and craft.',
      '## What clients see',
      'Everything visible remains handcrafted. Type is set by hand, frames are graded by eye, terminals are reviewed line by line. Algorithmic precision is the method; the surface stays human.',
      'That is the balance we hold ourselves to. If you can see the machine, we have not finished.',
    ],
  },
  {
    slug: 'off-market-dubai',
    title: 'Off-market Dubai — Visual intelligence for a build phase',
    category: 'Real Estate',
    date: 'Sep 2026',
    readTime: '6 min read',
    excerpt: 'How construction tracking and quiet visuals give investors confidence before the first stone is set.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/9d3d8e18-af7d-452a-86fd-c29accde9479.jpg',
    href: '/journal/off-market-dubai',
    body: [
      'Off-market real estate runs on trust. Investors are asked to commit before there is anything to see — often from another continent.',
      '## Showing progress, not promises',
      'Visual intelligence closes that gap. We connect build-phase data to a private terminal, so investors follow progress as it happens: milestones, site imagery and financial updates in one calm view.',
      'The visuals are deliberately quiet. No renderings that oversell, no dramatic skies. Just an honest, editorial picture of how the building is becoming real.',
      '## Why it works',
      'When investors can see the work, they stop asking for reassurance. Allocations close faster, and relationships last beyond a single project.',
    ],
  },
  {
    slug: 'haute-parfumerie-as-a-system',
    title: 'Haute parfumerie as a system and an investment',
    category: 'Luxury',
    date: 'Aug 2026',
    readTime: '5 min read',
    excerpt: 'Why we treat luxury products as assets: built once, operated for years, valued as equity.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/2fe92a8f-9a7d-49f2-8b47-a7a37c613a74.jpg',
    href: '/journal/haute-parfumerie-as-a-system',
    body: [
      'A fragrance is usually launched like a campaign: a burst of attention, then a slow fade. We think of it differently.',
      '## Built once, operated for years',
      'A haute parfumerie house is a system — identity, objects, film, distribution and the environment where collectors meet the work. Built properly, it compounds in value every year it is operated.',
      'That is why LAEBEL Ventures often takes equity in the houses we build. Our incentives match the founder’s: not a launch, but a lasting asset.',
      '## The quiet advantage',
      'In luxury, restraint is a strategy. The less a house has to shout, the more it is believed.',
    ],
  },
  {
    slug: 'equity-over-hourly-rates',
    title: 'Equity over hourly rates — How LAEBEL Ventures partners',
    category: 'Ventures',
    date: 'Jul 2026',
    readTime: '7 min read',
    excerpt: 'Trading media power and infrastructure for real shares, and why it aligns every decision.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/d43c9c82-a0ae-4e28-8dcb-3023a2b60eb1.jpg',
    href: '/journal/equity-over-hourly-rates',
    body: [
      'We do not sell time. Hourly rates reward slowness and put the studio on the opposite side of the table from the founder.',
      '## How it works',
      'Through LAEBEL Ventures, we contribute brand building, AI infrastructure and media reach — and receive real shares in return. Sometimes that is a personal brand, sometimes a company, sometimes a real asset in hospitality or longevity.',
      'Every partnership is structured simply: a clear contribution, a clear stake, and a long horizon.',
      '## Why it aligns',
      'When we own part of the outcome, every decision is made as an owner would make it. Quietly, precisely, and for the long term.',
    ],
  },
  {
    slug: 'longevity-infrastructure',
    title: 'Longevity infrastructure and the D 0|1 principle',
    category: 'Health',
    date: 'Jun 2026',
    readTime: '4 min read',
    excerpt: 'Biological optimisation and neuro-performance, designed as spaces and platforms rather than trends.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/001a218c-104a-4573-9af4-4109093dcef2.jpg',
    href: '/journal/longevity-infrastructure',
    body: [
      'Longevity is often sold as a trend. We approach it as infrastructure: spaces, platforms and routines that make better health the default.',
      '## The D 0|1 principle',
      'Every element is either on or off — designed in or left out. No decoration, no noise. The result is environments that feel like medicine and like rest at the same time.',
      'From residences in Mallorca to member platforms, the same principle applies: remove friction until only what works remains.',
    ],
  },
  {
    slug: 'building-in-days',
    title: 'Building in minutes what others need months for',
    category: 'Studios',
    date: 'May 2026',
    readTime: '9 min read',
    excerpt: 'How a single source of origin turns a brief into a live terminal, film and identity within one sprint.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/3b4f74dd-c57e-4ee6-95e8-b77ab81f2a33.jpg',
    href: '/journal/building-in-days',
    body: [
      'Most projects slow down at the handovers — between strategy and design, design and build, build and launch. We remove the handovers.',
      '## One source',
      'At LAEBEL, identity, terminal, film and distribution come from the same team and the same system. Decisions are made once and carried through everywhere.',
      'Our AI-OS handles the repetitive work in between, so a brief can become a live terminal, a film and an identity within a single sprint.',
      '## What speed is for',
      'Speed is not the goal; it is what makes craft affordable. The time we save goes back into the details nobody else has time for.',
    ],
  },
]
